import type ArrayProxy from '@ember/array/proxy';
import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import { isBlank } from '@ember/utils';
import type StoreService from '@ember-data/store';
import type { TaskInstance } from 'ember-concurrency';
import { all, hash, restartableTask } from 'ember-concurrency';
import type AgreementModel from 'garaje/models/agreement';
import type ConfigModel from 'garaje/models/config';
import type InviteModel from 'garaje/models/invite';
import type LocationModel from 'garaje/models/location';
import type SignInFieldModel from 'garaje/models/sign-in-field';
import type TenantModel from 'garaje/models/tenant';
import type UserModel from 'garaje/models/user';
import type ProtectedController from 'garaje/pods/protected/controller';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlowService from 'garaje/services/flow';
import type SkinnyLocationsService from 'garaje/services/skinny-locations';
import type StateService from 'garaje/services/state';
import dirtyCheck from 'garaje/utils/decorators/dirty-check';
import { loadAsyncRelationship } from 'garaje/utils/load-async-relationship';
import { getStatusCode } from 'garaje/utils/parse-error';
import throwUnlessTaskDidCancel from 'garaje/utils/throw-unless-task-did-cancel';
import type { RecordArray } from 'garaje/utils/type-utils';

import type { VisitorsInvitesRouteModel } from '../route';

import type VisitorsInvitesShowController from './controller';

interface VisitorsInvitesShowRouteParams {
  invite_id: string;
}

export interface VisitorsInvitesShowRouteModel {
  agreements: ArrayProxy<AgreementModel> | undefined;
  blocklistContacts: ArrayProxy<UserModel>;
  config: ConfigModel;
  connectedTenants: RecordArray<TenantModel>;
  invite: InviteModel;
  location: LocationModel;
  signInFields: ArrayProxy<SignInFieldModel> | undefined;
  loadFlowDataTask: VisitorsInvitesRouteModel['loadFlowDataTask'];
}

@dirtyCheck
class VisitorsInvitesShowRoute extends Route {
  @service declare featureFlags: FeatureFlagsService;
  @service declare flow: FlowService;
  @service declare router: RouterService;
  @service declare skinnyLocations: SkinnyLocationsService;
  @service declare state: StateService;
  @service declare store: StoreService;

  // start: dirty-check overrides
  _dirtyObject = 'changeset';
  _dirtyAttr = 'isDirty';
  // end: dirty-check overrides

  model(params: VisitorsInvitesShowRouteParams): TaskInstance<VisitorsInvitesShowRouteModel | undefined> {
    return this.modelTask.perform(params);
  }

  modelTask = restartableTask(
    {
      cancelOn: 'deactivate',
    },
    async ({ invite_id }: VisitorsInvitesShowRouteParams) => {
      const location = this.state.currentLocation;
      const blocklistContacts = await location.blocklistContacts;
      // Preload custom fields for flows. Since we use the fields to
      // build the UI, we want to make sure the data is loaded before
      // rendering something.
      const { connectedTenants, loadFlowDataTask } = <VisitorsInvitesRouteModel>this.modelFor('visitors.invites');

      // TODO: @heroiceric
      // This ensures we only reload config if it was already loaded. If we try
      // to reload when the config has not been previously loaded, we get an
      // error.
      //
      // I think this problem should go away when we're able to upgrade to newer
      // versions of ember data.
      if (location.belongsTo('config').value()) {
        await location.belongsTo('config').reload();
      }

      const inviteInclude = [
        'agreeable-ndas',
        'agreeable-ndas.agreement',
        'parent-invite-context',
        'child-invite-locations',
        'flow',
        'flow-version',
        'flow.flow-questionnaires',
        'flow.flow-questionnaires.questionnaire',
        'questionnaire-response-summary',
        'visit-approval',
        'visit-approval.screener-results',
        'visit-approval.screener-reviews',
        'visitor-profile',
      ];
      const fields = {
        'visitor-profiles': 'id',
      };

      if (connectedTenants.length) inviteInclude.push('multi-tenancy-visitor-notification-logs');

      if (this.state.features?.canAccessMultipleHosts) {
        inviteInclude.push('additional-hosts');
      }

      let invite;

      try {
        invite = await this.store.findRecord('invite', invite_id, {
          reload: true,
          include: inviteInclude.join(),
          adapterOptions: { fields },
        });

        await location.matchupInviteOrEntryFieldsWithLocationConfig(invite);
      } catch (e) {
        if (getStatusCode(e) === 410) {
          this.router.transitionTo('visitors.invites.deleted');

          return;
        } else {
          throw e;
        }
      }

      const config = await location.config;

      const inviteLocation = await invite?.location;
      if (inviteLocation) {
        this.maybeSwitchCurrentLocation(location, inviteLocation);
      }

      this.skinnyLocations.loadAllTask.perform().catch(throwUnlessTaskDidCancel);
      await this.flow.initService();

      let agreements: ArrayProxy<AgreementModel> | undefined;
      let signInFields: ArrayProxy<SignInFieldModel> | undefined;

      // [SQ-5019]
      // V2 endpoint includes associated IDs only. Now, fetch actual records.
      const childInviteLocations = invite.hasMany('childInviteLocations').ids();
      const additionalHosts = invite.hasMany('additionalHosts').ids();

      await all([
        ...childInviteLocations.map((relId) => this.store.findRecord('location', relId)),
        ...additionalHosts.map((relId) => this.store.findRecord('employee', relId)),
      ]);

      // load async flow
      const flow = await loadAsyncRelationship(invite, 'flow');
      // load async recurring invite
      await loadAsyncRelationship(invite, 'recurringInvite');

      if (flow) {
        const agreementPage = await flow.agreementPage;
        if (agreementPage && agreementPage.enabled) {
          agreements = await agreementPage.agreements;
        }

        const signInFieldPage = await flow.signInFieldPage;
        if (signInFieldPage) {
          // Determine if this invite should use custom fields
          if (this.shouldUseCustomSignInFields(invite)) {
            // Fetch from invite-specific endpoint
            const includes = [
              'sign-in-field-actions',
              'actionable-sign-in-field-actions',
              'actionable-sign-in-fields',
              'flow-version',
            ].join(',');

            signInFields = await this.store.query('invite-sign-in-field', {
              inviteId: invite.id,
              include: includes,
            });
          } else {
            // Use standard flow-based fields
            signInFields = await signInFieldPage.signInFields;
          }
        }

        // Load document-upload-page with its custom templates for the document review tab.
        // The sync hasMany on documentUploadPage won't auto-fetch templates, so we
        // reload the flow with the nested include to sideload them into the store.
        if (this.featureFlags.isEnabled('vis-prereg-document-uploads')) {
          const reloadedFlow = await this.store.findRecord('flow', flow.id, {
            include: 'document-upload-page,document-upload-page.custom-user-document-templates',
            reload: true,
          });
          const page = reloadedFlow.belongsTo('documentUploadPage').value();
          if (page) {
            const adapter = this.store.adapterFor('document-upload-page') as unknown as {
              fetchConfigurations(id: string): Promise<Record<string, unknown>>;
            };
            const payload = await adapter.fetchConfigurations(page.id);
            this.store.pushPayload(payload);
          }
        }

        if (flow.isProtect) {
          await this.flow.getFlowWithIncludes(flow.id);
        }
      }

      if (isBlank(invite.additionalGuests)) {
        invite.additionalGuests = 0;
      }

      return hash({
        agreements,
        blocklistContacts,
        config,
        connectedTenants,
        invite,
        location,
        signInFields,
        loadFlowDataTask,
      });
    },
  );

  setupController(
    controller: VisitorsInvitesShowController,
    model: VisitorsInvitesShowRouteModel,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);
    controller.setupChangeset();

    void model.loadFlowDataTask?.last?.then(() => {
      controller.loadVisitorDocumentsTask.perform().catch(throwUnlessTaskDidCancel);
    });
  }

  maybeSwitchCurrentLocation(currentLocation: LocationModel, inviteLocation: LocationModel): void {
    if (currentLocation.id !== inviteLocation.id) {
      // eslint-disable-next-line ember/no-controller-access-in-routes
      const protectedController = <ProtectedController>this.controllerFor('protected');
      protectedController.send('switchLocation', inviteLocation.id);
    }
  }

  /**
   * Determines whether to fetch sign-in fields from the invite-specific endpoint
   * @param invite - The invite model
   * @returns true if should use custom endpoint, false otherwise
   */
  shouldUseCustomSignInFields(invite: InviteModel): boolean {
    // Check split flag first
    if (!this.featureFlags.isEnabled('ava-versioned-fields')) {
      return false;
    }
    // Check if invite has a flow-version relationship
    const flowVersionId = invite.belongsTo('flowVersion').id();
    return !!flowVersionId;
  }
}

export default VisitorsInvitesShowRoute;
